import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import Plotly from "plotly.js-dist-min";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./MethanolChart.css";
import { buildNaAnnotation } from "./plotlyHelpers";
// ── Constants ─────────────────────────────────────────────────
const CHART_HEIGHT = 245;
const BAR_SLOT_WIDTH = 34;
const FIXED_BAR_PX = 17; // match the original Chart.js bar pixel width
const PLOT_MARGIN_H = 75; // left (65) + right (10) Plotly margins
const X_AXIS_TRAILING_SLOTS = 1;
const MIN_CONTAINER_WIDTH = 420;
const CHART_MARGIN_OFFSET = 70;
const MAX_BAR_WIDTH_FRACTION = 0.9;
const CHART_MARGIN = { b: 40, l: 65, r: 10, t: 5 };
const Y_AXIS_RANGE_PADDING = 0.5;
const AXIS_FONT_SIZE = 11;
const AXIS_FONT_FAMILY = "Noto Sans, sans-serif";
const AXIS_TICK_COLOR = "#808080";
const GRID_COLOR = "#CCCCCC";
const GRID_LINE_WIDTH = 1;
const ZEROLINE_COLOR = "#000000";
const CHART_BG = "rgba(0,0,0,0)";
const LEGEND_DOT_COLOR = "#1AC69E";
const LEGEND_LABEL_CLASSES = "whitespace-nowrap font-['Noto_Sans',sans-serif] text-[14px] font-semibold leading-5 text-[#3F3F3F]";
// ── Component ─────────────────────────────────────────────────
const MethanolBarChart = ({ barColor, data: chartData, isEnergyBarChart = false, legendLabel, testId, unit, xAxisLabel, yAxisMax, yAxisStepSize }) => {
    const containerRef = useRef(null);
    const plotRef = useRef(null);
    const [viewportWidth, setViewportWidth] = useState(700);
    const [customTooltip, setCustomTooltip] = useState(null);
    const plotInitialized = useRef(false);
    // Stable ref so hover handler always sees latest data without re-binding
    const chartDataRef = useRef(chartData);
    chartDataRef.current = chartData;
    useEffect(() => {
        if (!containerRef.current)
            return;
        const setWidth = () => {
            if (!containerRef.current)
                return;
            setViewportWidth(Math.max(containerRef.current.clientWidth, MIN_CONTAINER_WIDTH));
        };
        setWidth();
        const resizeObserver = new ResizeObserver(setWidth);
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, []);
    const chartWidth = Math.max(viewportWidth, (chartData.length + X_AXIS_TRAILING_SLOTS) * BAR_SLOT_WIDTH + CHART_MARGIN_OFFSET);
    // Compute bar width as a fraction of the category slot so it always renders
    // at the same pixel width as the original Chart.js FIXED_BAR_WIDTH (17px).
    const totalSlots = chartData.length + X_AXIS_TRAILING_SLOTS;
    const pixelsPerSlot = (chartWidth - PLOT_MARGIN_H) / totalSlots;
    const barWidthFraction = Math.min(FIXED_BAR_PX / pixelsPerSlot, MAX_BAR_WIDTH_FRACTION);
    // ── Build Plotly trace ─────────────────────────────────────
    const traces = useMemo(() => {
        const xLabels = [...chartData.map((d) => d.date), ""];
        const yValues = [...chartData.map((d) => (d.value === "N/A" ? null : d.value)), null];
        return [
            {
                hoverinfo: "none",
                marker: { color: barColor, line: { width: 0 } },
                type: "bar",
                width: barWidthFraction,
                x: xLabels,
                y: yValues
            }
        ];
    }, [chartData, barColor, barWidthFraction]);
    // Plotly annotations for N/A days
    const naAnnotations = useMemo(() => chartData.filter((d) => d.value === "N/A").map((d) => buildNaAnnotation(d.date)), [chartData]);
    // ── Plotly layout ──────────────────────────────────────────
    const layout = useMemo(() => ({
        annotations: naAnnotations,
        barcornerradius: 4,
        height: CHART_HEIGHT,
        hovermode: "x",
        margin: CHART_MARGIN,
        paper_bgcolor: CHART_BG,
        plot_bgcolor: CHART_BG,
        showlegend: false,
        width: chartWidth,
        xaxis: {
            fixedrange: true,
            showgrid: false,
            tickfont: { color: AXIS_TICK_COLOR, family: AXIS_FONT_FAMILY, size: AXIS_FONT_SIZE },
            zeroline: false
        },
        yaxis: {
            autorange: false,
            dtick: yAxisStepSize,
            fixedrange: true,
            gridcolor: GRID_COLOR,
            gridwidth: GRID_LINE_WIDTH,
            range: [0, yAxisMax + yAxisStepSize * Y_AXIS_RANGE_PADDING],
            tickfont: { color: AXIS_TICK_COLOR, family: AXIS_FONT_FAMILY, size: AXIS_FONT_SIZE },
            tickformat: ",",
            ticksuffix: ` ${unit}`,
            zeroline: true,
            zerolinecolor: ZEROLINE_COLOR,
            zerolinewidth: GRID_LINE_WIDTH
        }
    }), [naAnnotations, chartWidth, unit, yAxisMax, yAxisStepSize]);
    const plotlyConfig = useMemo(() => ({ displayModeBar: false, responsive: false }), []);
    // ── Hover handlers (use ref so they stay stable) ───────────
    const handleHover = useCallback((eventData) => {
        const pt = eventData.points?.[0];
        if (!pt)
            return;
        const xDate = pt.x;
        const datum = chartDataRef.current.find((d) => d.date === xDate);
        if (!datum || datum.value === "N/A") {
            setCustomTooltip(null);
            return;
        }
        const containerBounds = containerRef.current?.getBoundingClientRect();
        if (!containerBounds)
            return;
        const mouseEvent = eventData.event;
        setCustomTooltip({
            date: datum.tooltipDate,
            left: mouseEvent.clientX - containerBounds.left,
            top: mouseEvent.clientY - containerBounds.top,
            value: datum.value
        });
    }, []);
    const handleUnhover = useCallback(() => setCustomTooltip(null), []);
    // ── Initial plot (mount only) ──────────────────────────────
    useEffect(() => {
        if (!plotRef.current)
            return;
        const el = plotRef.current;
        Plotly.newPlot(el, traces, layout, plotlyConfig).then(() => {
            el.on("plotly_hover", handleHover);
            el.on("plotly_unhover", handleUnhover);
            plotInitialized.current = true;
        });
        return () => {
            Plotly.purge(el);
            plotInitialized.current = false;
        };
    }, []);
    // ── Update plot when data / size changes ───────────────────
    useEffect(() => {
        if (!plotRef.current || !plotInitialized.current)
            return;
        Plotly.react(plotRef.current, traces, layout, plotlyConfig);
    }, [traces, layout, plotlyConfig]);
    return (_jsxs("div", { className: "w-full", "data-test": testId, children: [_jsxs("div", { className: "mx-auto mb-1 flex h-[28px] items-center justify-center", "data-test": `${testId}-legend`, children: [_jsx("span", { className: "mr-1 inline-flex h-[16px] shrink-0 items-end align-bottom font-['Noto_Sans',sans-serif] text-[12px] font-normal leading-4 text-[#666565]", children: "Legend:" }), _jsxs("span", { className: "inline-flex h-[28px] items-center gap-2 p-1", children: [_jsx("span", { className: `inline-block h-[8px] w-[8px] shrink-0 rounded-full bg-[${LEGEND_DOT_COLOR}]`, "data-test": `${testId}-legend-color-dot` }), isEnergyBarChart ? (_jsx("span", { className: LEGEND_LABEL_CLASSES, "data-test": `${testId}-legend-label`, children: legendLabel || "kWh" })) : (_jsx(_Fragment, { children: _jsx("span", { className: LEGEND_LABEL_CLASSES, "data-test": `${testId}-legend-label`, children: "Methanol Consumed (L)" }) }))] })] }), _jsxs("div", { className: "relative w-full", "data-test": `${testId}-resize-container`, ref: containerRef, children: [customTooltip && (_jsxs("div", { className: "pointer-events-none absolute z-50 inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-[#e0e0e0] bg-white px-3 py-[7px] text-xs shadow-[0_4px_12px_rgb(0_0_0/12%)] [transform:translate(-50%,calc(-100%-12px))]", "data-test": `${testId}-tooltip`, style: { left: customTooltip.left, top: customTooltip.top }, children: [_jsxs("span", { className: "font-normal text-gray-500", "data-test": `${testId}-tooltip-date`, children: [customTooltip.date, ":"] }), _jsxs("span", { className: "font-bold text-gray-900", "data-test": `${testId}-tooltip-value`, children: [customTooltip.value, " ", unit] }), _jsx("span", { className: "absolute bottom-[-8px] left-1/2 h-0 w-0 -translate-x-1/2 border-l-[7px] border-l-transparent border-r-[7px] border-r-transparent border-t-[8px] border-t-[#e0e0e0] after:absolute after:bottom-[2px] after:left-1/2 after:h-0 after:w-0 after:-translate-x-1/2 after:border-l-[6px] after:border-l-transparent after:border-r-[6px] after:border-r-transparent after:border-t-[7px] after:border-t-white after:content-['']" })] })), _jsx("div", { className: "methanol-chart-scroll", "data-test": `${testId}-scroll-container`, children: _jsx("div", { "data-test": `${testId}-chart-wrapper`, ref: plotRef, style: { height: `${CHART_HEIGHT}px`, width: `${chartWidth}px` } }) }), _jsx("div", { className: "pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 text-center font-['Noto_Sans',sans-serif] text-[14px] font-normal leading-5 text-[#666565]", "data-test": `${testId}-x-axis-label`, children: xAxisLabel })] })] }));
};
export default MethanolBarChart;
